import * as React from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import styled from "styled-components";

const Direction = styled.p`
	white-space: pre-wrap;
`;

const LinkArea = styled.div`
	margin-top: 24px;
	text-align: center;
`;

const TextButton = styled.button`
	background: none;
	border: none;
	color: blue;
	padding: 0;
	text-decoration: underline;
`;

export type LoginMFAVerifyPageProps = {
	error?: string;
	isPending: boolean;
	onChangeMode: () => void;
	onSubmit: (code: string, recovery: boolean) => Promise<void>;
};

/**
 * ログイン時の多要素認証
 * - 認証アプリのコード / リカバリコードを切り替えて入力する
 */
export const LoginMFAVerifyPage = React.memo((props: LoginMFAVerifyPageProps) => {
	const { error, isPending, onChangeMode, onSubmit } = props;
	const [useRecoveryCode, setUseRecoveryCode] = React.useState<boolean>(false);
	const [code, setCode] = React.useState<string>("");

	const onChangeCode = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value.trim()),
		[],
	);
	const onToggleRecoveryCode = React.useCallback(() => {
		setUseRecoveryCode((prev) => !prev);
		setCode("");
		onChangeMode();
	}, [onChangeMode]);

	const isDisabled = isPending || (useRecoveryCode ? code === "" : code.length !== 6);

	/**
	 * Enterキー押下時のブラウザ標準のフォーム送信（ページ遷移）を抑止する
	 */
	const onFormSubmit = React.useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();
			if (isDisabled) return;
			onSubmit(code, useRecoveryCode).catch(() => setCode(""));
		},
		[code, isDisabled, onSubmit, useRecoveryCode],
	);

	return (
		<Card className="border-0 mx-auto my-5" style={{ maxWidth: "540px" }}>
			<Card.Body>
				{error && <Alert variant={"danger"}>{error}</Alert>}
				<Direction>
					{useRecoveryCode
						? "リカバリコードを入力してください。\nリカバリコードは1つにつき1回のみ使用できます。"
						: "認証アプリに表示された認証コードを入力してください"}
				</Direction>
				<Form noValidate onSubmit={onFormSubmit}>
					<Form.Group>
						<Form.Label>{useRecoveryCode ? "リカバリコード" : "認証コード"}</Form.Label>
						<Form.Control
							autoComplete="one-time-code"
							autoFocus
							inputMode={useRecoveryCode ? "text" : "numeric"}
							isInvalid={!!error}
							maxLength={useRecoveryCode ? undefined : 6}
							onChange={onChangeCode}
							placeholder={useRecoveryCode ? "" : "認証コード(6桁)"}
							value={code}
						/>
					</Form.Group>
					<div className="my-4">
						<Button className="w-100" variant={"dark"} type="submit" disabled={isDisabled}>
							コードを認証
						</Button>
					</div>
				</Form>
				<LinkArea>
					<TextButton type="button" onClick={onToggleRecoveryCode}>
						{useRecoveryCode ? "認証アプリのコードを使用する" : "リカバリコードを使用する"}
					</TextButton>
				</LinkArea>
			</Card.Body>
		</Card>
	);
});
